<template>
  <div class="select-dates-btn-wrapper">
    <button
      id="toggle-datepicker-btn"
      class="btn btn-tertiary"
      :class="{ 'border-dark': isDatepickerOpened }"
    >
      <div
        data-testid="toggle-datepicker-modal"
        class="btn btn-inline btn-toggle-datepicker"
        aria-label="Select dates"
      >
        <i class="icon-calendar" :class="[mainIconSize, { 'color-primary': isDatepickerOpened }]" />
        <div class="dates-heading-wrapper">
          <span v-if="areDatesSelected" class="selected-dates-heading">{{ datesHeading }}</span>
          <span v-else-if="isDevice('tablet-l')" class="default-dates-heading">{{translate('offDealsDatesPlaceholderShort')}}</span>
          <span v-else class="default-dates-heading">{{
            translate('offDealsDatesPlaceholderLarge')
          }}</span>
        </div>
      </div>
      <button
        v-if="!isDevice('tablet-l') && areDatesSelected"
        data-testid="clear-dates-btn"
        class="btn btn-inline btn-clear-datepicker"
        aria-label="Reset dates"
        @click="clearDates()"
      >
        <i class="icon-cross xs clear-dates-icon" />
      </button>
    </button>
    <!-- Select dates dropdown -->
    <PortalTarget name="select-dates-btn-wrapper" />
    <Portal :to="getPortalTarget()">
      <DatepickerModal
        class="datepicker-modal"
        :is-opened="isDatepickerOpened"
        :is-full-screen="isDevice('tablet-l')"
        :start="start ? new Date(start) : null"
        :end="end ? new Date(end) : null"
        :min-date="today"
        @toggle="toggleDatepicker"
        @clear="clearDates"
        @select="selectDates"
        @apply="applyDates"
      />
    </Portal>
  </div>
</template>

<script>
import { DateFactory } from 'rhg-vue-common'
import { media } from '@/infrastructure/Utils'
import DatepickerModal from '@/components/offers/DatepickerModal'
import { maxLengthStayErrorModal } from '@/helpers/modalMaxStay'
import { MAX_STAY } from '@/infrastructure/Configuration'

const getToday = () => {
  return DateFactory.dateObject().format('YYYY-MM-DD')
}

export default {
  name: 'OffersDatesFilter',
  components: {
    DatepickerModal
  },
  emits: ['datesChange'],
  props: {
    selectedDatesRange: {
      type: Object,
      required: true
    },
    mainIconSize: {
      type: String,
      required: false,
      default: 'sm'
    }
  },
  mounted() {
    window.addEventListener('click', this.handleCloseModal)
    window.addEventListener('resize', this.closeDatepickerOnResize)
  },
  destroyed() {
    window.removeEventListener('click', this.handleCloseModal)
    window.removeEventListener('resize', this.closeDatepickerOnResize)
  },
  data() {
    return {
      isDatepickerOpened: false,
      start: null,
      end: null,
      today: getToday(),
      dateFormat: 'D MMM YYYY'
    }
  },
  computed: {
    datesHeading() {
      return `${this.formatDate(this.start)} - ${this.formatDate(this.end)}`
    },
    areDatesSelected() {
      return this.start && this.end
    }
  },
  watch: {
    selectedDatesRange(value) {
      this.setDates(value)
    }
  },
  methods: {
    setDates(dates) {
      const { start, end } = dates
      this.start = start || null
      this.end = end || null
    },
    formatDate(date) {
      return DateFactory.formatDate(date, this.dateFormat)
    },
    toggleDatepicker(val) {
      this.isDatepickerOpened = typeof val === 'boolean' ? val : !this.isDatepickerOpened
    },
    closeDatepickerOnResize(_e) {
      this.toggleDatepicker(false)
    },
    applyDates() {
      this.$emit('datesChange', { start: this.start, end: this.end })
      this.toggleDatepicker(false)
    },
    clearDates() {
      this.start = null
      this.end = null
      this.applyDates()
    },
    selectDates({ start, end }) {
      const diffMaxStay = DateFactory.daysBetween(start, end)
      if (diffMaxStay > MAX_STAY) {
        maxLengthStayErrorModal()
        this.start = null
        this.end = null
      } else {
        this.start = start || null
        this.end = end || null
      }
    },
    isDevice(type) {
      const clientWidth = window.innerWidth
      return clientWidth <= media(type)
    },
    getPortalTarget() {
      const clientWidth = window.screen.availWidth
      return clientWidth > media('tablet-l') ? 'select-dates-btn-wrapper' : 'modal-root-dates'
    },
    handleCloseModal(e) {
      const buttonFilters = document.getElementById('toggle-datepicker-btn')
      const offersLocationModal = document.getElementById('modal-datepicker-offers')

      if (offersLocationModal) {
        this.toggleDatepicker(offersLocationModal.contains(e.target))
      } else if (buttonFilters) {
        this.toggleDatepicker(buttonFilters.contains(e.target))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.select-dates-btn-wrapper {
  @media (max-width: media('tablet-l')) {
    width: 100%;
  }
}

.default-dates-heading,
.icon-calendar {
  color: color('dark-grey');
}

.color-primary {
  color: color('primary') !important;
}
</style>
